import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Attention } from '@travel/icons/ui';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { GalleryIcon } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';
import Media from 'components/Media';
import PhotoGallery from 'components/PhotoGallery';
import Swiper from 'components/Swiper';
import { getDetailSummary, getGalleryCoverInformation } from 'store/providerInformation/selectors';
import useDialogHandler from '../../../../hooks/useDialogHandler';
import DetailSummary from '../DetailSummary';
import ThumbnailList from '../ThumbnailList';

import { Translate } from 'core/translate';

import styles from './providerInformationHeader.module.scss';

type ProviderInformationHeaderProps = {
  /** on what parent page this component lives */
  onPage: 'providerInfoUndated' | 'providerInfoDated';
  className?: string;
  /** String to define the current provider id */
  pageIdentifier: string;
  /** Flag to define whether the header is floating or not (Mobile only) */
  isHeaderFloating?: boolean;
  /** Callback to be called when header floating state has been changed (Mobile only)  */
  onChangeHeaderFloatingState?: (isFloating: boolean) => void;
  /** RAT page name  */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
};

function ProviderInformationHeader(props: ProviderInformationHeaderProps) {
  const {
    pageIdentifier,
    className,
    pageName,
    onPage,
    isHeaderFloating,
    onChangeHeaderFloatingState,
  } = props;

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;

  const [initImageIndex, setInitImageIndex] = useState(0);

  const {
    isOpen: isOpenGallery,
    onClose: onCloseGallery,
    onOpen: onOpenGallery,
  } = useDialogHandler(false);

  const handleOnOpenGallery = (index: number) => () => {
    setInitImageIndex(index);
    onOpenGallery();
  };

  const detailSummary = useSelector(getDetailSummary(pageIdentifier));
  const galleryCoverInformation = useSelector(getGalleryCoverInformation);

  const inActiveLabel = detailSummary.isActive === false && (
    <div className={styles.inactiveLabel}>
      <Attention className={styles.icon} size={16} />
      <Translate id="Provider_Info.Provider_Details.Inactive" />
    </div>
  );

  return (
    <div className={cx(styles.wrapper, className)} data-testid="providerInformationHeader-wrapper">
      {!isMobile && inActiveLabel}
      {!isMobile && (
        <DetailSummary
          pageName={pageName}
          pageIdentifier={pageIdentifier}
          isShare={galleryCoverInformation.isShare}
        />
      )}

      {isMobile && (
        <div className={styles.container}>
          <div className={styles.galleryCoverWrapper}>
            {isNotEmptyArray(detailSummary.galleryData) ? (
              <>
                {detailSummary.photosCount === 1 ? (
                  <div className={styles.galleryDetails}>
                    <Media
                      key={detailSummary.galleryData[0].url}
                      wrapperClassName={styles.mediaWrapper}
                      media={detailSummary.galleryData[0]}
                      sizes="100vw"
                      onClick={onOpenGallery}
                      isDisableLazyload={true}
                    />
                  </div>
                ) : (
                  <Swiper
                    className={styles.galleryDetails}
                    paginationType="swipe"
                    shouldRenderAllChild={false}
                  >
                    {detailSummary.galleryData.map((media, index) => (
                      <Media
                        key={media.url}
                        wrapperClassName={styles.mediaWrapper}
                        media={media}
                        sizes="100vw"
                        onClick={handleOnOpenGallery(index)}
                        isDisableLazyload={index === 0}
                      />
                    ))}
                  </Swiper>
                )}
                <GalleryIcon
                  photosCount={detailSummary.photosCount}
                  className={styles.galleryIcon}
                  onClick={onOpenGallery}
                />
              </>
            ) : (
              <div className={styles.galleryDetails}>
                <Media sizes="100vw" isMediaOnly={true} />
              </div>
            )}
          </div>
          {inActiveLabel}
        </div>
      )}

      {!isMobile && (
        <ThumbnailList
          onPage={onPage}
          media={detailSummary.galleryData}
          isDisplaySeeAllPhotos={true}
          isDisplayPhotoGallery={true}
        />
      )}

      {isMobile && (
        <DetailSummary
          pageName={pageName}
          pageIdentifier={pageIdentifier}
          isShare={galleryCoverInformation.isShare}
          isHeaderFloating={isHeaderFloating}
          onChangeHeaderFloatingState={onChangeHeaderFloatingState}
        />
      )}

      <PhotoGallery
        galleryData={detailSummary.galleryData}
        isOpenPhotoGallery={isOpenGallery}
        onClosePhotoGallery={onCloseGallery}
        initImageIndex={initImageIndex}
      />

      {isOpenGallery && (
        <DataLayer
          pageName={`${pageName}:hotel_photo_gallery_popup`}
          pageType="shop_item"
          siteSection="hotel"
        />
      )}
    </div>
  );
}

export default React.memo(ProviderInformationHeader);
